<template>
  <aside class="right-sidebar">
    <div class="sidebar-content" v-if="isAdmin && routeName === 'adminsupport'">
      <div class="sidebar-section">
        <h3 class="sidebar-title">
          <v-icon size="16" class="mr-1" color="#EA5545">mdi-cog</v-icon>
          {{ t('menus.rightSidebar.adminKunde.ticketActionsTitle') }}
        </h3>
        <p class="sidebar-text" v-if="ticket">
          {{ t('menus.rightSidebar.adminKunde.ticketActionsDescription', { id: ticket.sup_id }) }}
        </p>
        <p class="sidebar-text" v-else>
          {{ t('menus.rightSidebar.adminKunde.ticketLoading') }}
        </p>
      </div>

      <v-divider class="my-4"></v-divider>

      <!-- Kundendaten-Bereich -->
      <div class="sidebar-section">
        <!-- <h4 class="sidebar-subtitle">
          <v-icon size="16" class="mr-1" color="#EA5545">mdi-account-search</v-icon>
          {{ t('menus.rightSidebar.adminKunde.customerDataTitle') }}
        </h4> -->

        <div v-if="ticket && ticket.myp_vertrags_nummer">
          <!-- <div class="sidebar-label">{{ t('menus.rightSidebar.adminKunde.contractNumberLabel') }}</div>
          <div class="sidebar-value">
            {{ ticket.myp_vertrags_nummer }}
          </div> -->
  
          <template v-if="canFetchKundendaten">
            <v-btn
              class="mt-2"
              color="#EA5545"
              variant="outlined"
              size="small"
              block
              @click="fetchKundendaten"
              :loading="isLoadingKundendaten"
              :disabled="isLoadingKundendaten"
            >
              <v-icon left size="16">mdi-magnify</v-icon>
              {{ t('menus.rightSidebar.adminKunde.fetchCustomerData') }}
            </v-btn>
          </template>
          <p v-else class="sidebar-hint">
            {{ t('menus.rightSidebar.adminKunde.restriction') }}
          </p>
        </div>
        <div v-else>
          <div class="sidebar-label muted">
            {{ t('menus.rightSidebar.adminKunde.noContractNumberTitle') }}
          </div>
          <div class="sidebar-value">
            {{ t('menus.rightSidebar.adminKunde.noContractNumberDescription') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Fallback: nichts anzeigen, wenn nicht auf Admin-Support-Route -->
    <div v-else class="sidebar-content sidebar-empty">
      <div class="sidebar-section">
        <h3 class="sidebar-title">
          <v-icon size="16" class="mr-1" color="#EA5545">mdi-shield-account</v-icon>
          {{ t('menus.rightSidebar.adminKunde.fallbackTitle') }}
        </h3>
        <p class="sidebar-text">{{ t('menus.rightSidebar.adminKunde.fallbackText') }}</p>
      </div>
    </div>

    <!-- Kundendaten-Dialog -->
    <v-dialog
      v-model="showKundendatenDialog"
      max-width="800"
    >
      <v-card class="kundendaten-card">
        <v-card-title>
          <span>{{ t('menus.rightSidebar.adminKunde.dialog.title') }}</span>
        </v-card-title>
        <v-btn
          icon
          variant="text"
          class="dialog-close-btn"
          @click="showKundendatenDialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-divider></v-divider>
        <v-card-text class="kundendaten-dialog-content">
          <div v-if="isLoadingKundendaten" class="state-block" data-state="loading">
            <v-progress-circular
              indeterminate
              color="#EA5545"
              size="40"
            ></v-progress-circular>
            <p class="state-text">{{ t('menus.rightSidebar.adminKunde.dialog.loading') }}</p>
          </div>
          <template v-else>
            <v-alert
              v-if="kundeError"
              type="error"
              variant="tonal"
              class="mb-4"
            >
              {{ kundeError }}
            </v-alert>

            <div v-if="kundeData">
              <div class="kundendaten-section-title">{{ t('menus.rightSidebar.adminKunde.dialog.sections.customer') }}</div>
              <div v-if="kundeInfoLines.length" class="kundendaten-details">
                <p
                  v-for="(line, index) in kundeInfoLines"
                  :key="index"
                  class="kundendaten-line"
                >
                  {{ line }}
                </p>
              </div>
              <pre v-else class="kundendaten-json">{{ prettyKundeData }}</pre>
            </div>

            <div v-if="abschlussData" class="mt-4">
              <div class="kundendaten-section-title">{{ t('menus.rightSidebar.adminKunde.dialog.sections.completion') }}</div>
              <pre class="kundendaten-json">{{ prettyAbschlussData }}</pre>
            </div>

            <div
              v-if="!kundeError && !kundeData && !abschlussData"
              class="mt-2"
            >
              {{ t('menus.rightSidebar.adminKunde.dialog.noData') }}
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="#EA5545" variant="text" @click="showKundendatenDialog = false">
            {{ t('menus.rightSidebar.adminKunde.dialog.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </aside>
</template>

<script setup>
import { inject, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'

const baseCalls = inject('baseCalls')
const userStore = inject('userStore')
const errorStore = inject('errorStore')
const { t } = useLanguage()

const route = useRoute()

const ticket = ref(null)
const isLoadingTicket = ref(false)
const isLoadingKundendaten = ref(false)
const showKundendatenDialog = ref(false)
const kundeError = ref('')
const kundeData = ref(null)
const abschlussData = ref(null)

const userData = computed(() => userStore?.userData || null)

const isAdmin = computed(() => {
  const u = userData.value
  if (!u || !u.user_admin) return false
  return Number(u.user_admin) === 1
})

const canFetchKundendaten = computed(() => {
  if (!ticket.value) {
    return false
  }
  return Number(ticket.value.user_company_id || 0) === 1
})

const routeName = computed(() => route.name)
const routeId = computed(() => {
  const rawId = route.params.id
  if (!rawId && rawId !== 0) return null
  const parsed = Number(rawId)
  return Number.isNaN(parsed) ? null : parsed
})

async function loadTicket() {
  if (!routeId.value || routeName.value !== 'adminsupport') {
    return
  }

  if (!isAdmin.value) {
    return
  }

  isLoadingTicket.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getadminsupportdetail',
      sup_id: routeId.value
    })

    if (response.data.success && response.data.support) {
      ticket.value = response.data.support
    } else {
      ticket.value = null
      const msg =
        response.data.message ||
        t('adminSupportIndex.errors.loadFailed')
      errorStore?.setError?.(true, msg)
    }
  } catch (err) {
    console.error('Error loading admin support detail (sidebar):', err)
    ticket.value = null
  } finally {
    isLoadingTicket.value = false
  }
}

async function fetchKundendaten() {
  if (!canFetchKundendaten.value) {
    return
  }
  if (!ticket.value || !ticket.value.myp_vertrags_nummer) {
    kundeError.value = t('menus.rightSidebar.adminKunde.errors.noContractNumber')
    showKundendatenDialog.value = true
    return
  }

  showKundendatenDialog.value = true
  isLoadingKundendaten.value = true
  kundeError.value = ''
  kundeData.value = null
  abschlussData.value = null

  try {
    const response = await baseCalls.clearmentPost({
      action: 'wtbGetKUnde',
      vertrag_nummer: ticket.value.myp_vertrags_nummer,
      kundennummer: ticket.value.user_company_kundennummer || undefined
    })

    const data = response?.data || {}

    if (!data.success) {
      kundeError.value = data.error || t('menus.rightSidebar.adminKunde.errors.fetchFailed')
      return
    }

    kundeData.value = normalizePayload(data.kunde || null)
    abschlussData.value = normalizePayload(data.abschluss || null)

    if (!kundeData.value && !abschlussData.value) {
      kundeError.value = t('menus.rightSidebar.adminKunde.dialog.noData')
    }
  } catch (err) {
    console.error('Error fetching Kundendaten (sidebar):', err)
    kundeError.value = t('menus.rightSidebar.adminKunde.errors.fetchException')
  } finally {
    isLoadingKundendaten.value = false
  }
}

onMounted(() => {
  loadTicket()
})

function normalizePayload(payload) {
  if (!payload) {
    return null
  }
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload)
    } catch (error) {
      console.warn('Unable to parse kundendaten payload', error)
      return { raw: payload }
    }
  }
  return payload
}

const kundeInfoLines = computed(() => {
  const data = kundeData.value
  if (!data) return []
  const lines = []
  if (data.user_vp_name) {
    lines.push(String(data.user_vp_name))
  }
  if (data.user_vn_name) {
    lines.push(String(data.user_vn_name))
  }
  if (data.user_strasse) {
    lines.push(String(data.user_strasse))
  }
  if (data.user_ort) {
    lines.push(String(data.user_ort))
  }
  return lines
})

const prettyKundeData = computed(() => {
  if (!kundeData.value) return ''
  if (typeof kundeData.value === 'string') return kundeData.value
  try {
    return JSON.stringify(kundeData.value, null, 2)
  } catch (error) {
    return String(kundeData.value)
  }
})

const prettyAbschlussData = computed(() => {
  if (!abschlussData.value) return ''
  if (typeof abschlussData.value === 'string') return abschlussData.value
  try {
    return JSON.stringify(abschlussData.value, null, 2)
  } catch (error) {
    return String(abschlussData.value)
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/modules/RightSidebarAdminKunde.css"></style>
